// The hole as geometry, built from the same generator the server runs: what you see is what the ball
// rolls on. One heightfield with a surface attribute (so the turf shader can mow the fairway, dry the
// rough and ripple the sand without a single texture file), a soft-edged water sheet, a flag that
// leans on the real wind, tee markers, distance posts and a tree line along the rough.
//
// Contract kept: `buildHole(hole)` still works (the `world` argument is optional and only adds the
// sky/sun/palette switch), and the names `ground`, `cup`, `flag` and `aim` are unchanged.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { SURFACES, sampleHeight, sampleSurface, type Hole } from '@shared/course';
import { mulberry32, seedFrom } from '@shared/rng';
import { themeFor, type Palette } from './theme';
import type { World } from './world';

const col = (hex: string) => new THREE.Color(hex);

/* ------------------------------------------------------------------ shaders */

const NOISE = /* glsl */ `
float h21(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453123); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
  float a = h21(i), b = h21(i+vec2(1.0,0.0)), c = h21(i+vec2(0.0,1.0)), d = h21(i+vec2(1.0,1.0));
  return mix(mix(a,b,f.x), mix(c,d,f.x), f.y);
}
float fbm(vec2 p){ return 0.6*vnoise(p) + 0.3*vnoise(p*2.3) + 0.1*vnoise(p*4.7); }
`;

// Everything that makes turf read as turf: mower stripes down the fairway, a cross-cut on the green,
// dry patches in the rough, rake ripples in the sand. All driven by world position and `aSurf`.
const TURF = /* glsl */ `
  float wTee   = 1.0 - clamp(abs(vSurf - 0.0), 0.0, 1.0);
  float wFair  = 1.0 - clamp(abs(vSurf - 1.0), 0.0, 1.0);
  float wGreen = 1.0 - clamp(abs(vSurf - 2.0), 0.0, 1.0);
  float wRough = 1.0 - clamp(abs(vSurf - 3.0), 0.0, 1.0);
  float wSand  = 1.0 - clamp(abs(vSurf - 4.0), 0.0, 1.0);

  float mow = smoothstep(0.25, 0.75, sin(vWPos.z * 0.40) * 0.5 + 0.5);
  diffuseColor.rgb *= mix(1.0, mix(0.935, 1.065, mow), clamp(wFair + wTee, 0.0, 1.0));
  float mowG = smoothstep(0.3, 0.7, sin((vWPos.x * 0.8 + vWPos.z * 0.8) * 1.1) * 0.5 + 0.5);
  diffuseColor.rgb *= mix(1.0, mix(0.965, 1.045, mowG), wGreen);

  float n = fbm(vWPos.xz * 0.55);
  diffuseColor.rgb *= 1.0 + (n - 0.5) * (0.26 * wRough + 0.08 * wFair);
  float dry = smoothstep(0.58, 0.88, fbm(vWPos.xz * 0.11));
  diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * vec3(1.20, 1.13, 0.84), wRough * dry * 0.8);

  float rip = sin(vWPos.x * 2.2 + fbm(vWPos.xz * 0.6) * 5.0) * 0.5 + 0.5;
  diffuseColor.rgb *= mix(1.0, mix(0.93, 1.06, rip), wSand);
  diffuseColor.rgb *= 0.95 + 0.10 * fbm(vWPos.xz * 0.04);
`;

function turfMaterial(): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.96, metalness: 0.0 });
  m.onBeforeCompile = (shader) => {
    shader.vertexShader = `attribute float aSurf;\nvarying float vSurf;\nvarying vec3 vWPos;\n${shader.vertexShader}`
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n vSurf = aSurf;\n vWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = `varying float vSurf;\nvarying vec3 vWPos;\n${NOISE}\n${shader.fragmentShader}`
      .replace('#include <color_fragment>', `#include <color_fragment>\n${TURF}`);
  };
  return m;
}

/* --------------------------------------------------------------------- hole */

export function buildHole(hole: Hole, world?: World): THREE.Group {
  const pal = world ? world.applyTheme(hole.brief.theme) : themeFor(hole.brief.theme);
  if (world) world.placeSun(hole);
  const group = new THREE.Group();
  group.name = `hole-${hole.index}`;
  const rnd = mulberry32(seedFrom(hole.seed, 'art'));

  /* ground: the generator's heightfield, coloured by surface and shaded by the turf shader */
  const geo = new THREE.PlaneGeometry(hole.widthM, hole.lengthM, hole.nx - 1, hole.nz - 1);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const surf = new Float32Array(pos.count);
  const C: Record<string, THREE.Color> = {};
  for (const s of SURFACES) C[s] = col(pal.turf[s]);
  for (let k = 0; k < pos.count; k++) {
    const i = k % hole.nx, j = Math.floor(k / hole.nx);
    const x = -hole.widthM / 2 + i * hole.cell, z = j * hole.cell;
    pos.setXYZ(k, x, hole.heights[j * hole.nx + i], z);
    const sid = hole.surfaces[j * hole.nx + i];
    const c = C[SURFACES[sid]];
    colors[k * 3] = c.r; colors[k * 3 + 1] = c.g; colors[k * 3 + 2] = c.b;
    surf[k] = sid;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('aSurf', new THREE.BufferAttribute(surf, 1));
  geo.computeVertexNormals();
  const ground = new THREE.Mesh(geo, turfMaterial());
  ground.name = 'ground';
  ground.receiveShadow = true;
  group.add(ground);

  /* a wide apron just below the grid, so the hole does not end in mid-air */
  const apron = new THREE.Mesh(
    new THREE.PlaneGeometry(2600, 2600),
    new THREE.MeshStandardMaterial({ color: col(pal.turf.oob).multiplyScalar(0.92), roughness: 1 }),
  );
  apron.rotation.x = -Math.PI / 2;
  apron.position.set(0, -1.4, hole.lengthM / 2);
  group.add(apron);

  const water = buildWater(hole, pal);
  if (water) group.add(water);
  group.add(buildCup(hole));
  group.add(buildFlag(hole, pal));
  group.add(buildTee(hole, pal));
  const posts = buildDistancePosts(hole, pal);
  if (posts) group.add(posts);
  const trees = buildTrees(hole, pal, rnd);
  if (trees) group.add(trees);
  return group;
}

/** Run every animated part of the hole: water, flag cloth, tree sway. Call once a frame. */
export function tickHole(group: THREE.Object3D, t: number, world?: World, wind?: { x: number; z: number }) {
  group.traverse((o) => {
    const fn = (o.userData as { tick?: (t: number, w?: World, wind?: { x: number; z: number }) => void }).tick;
    if (typeof fn === 'function') fn(t, world, wind);
  });
}

/* -------------------------------------------------------------------- water */

/** A sheet whose alpha comes from a blurred water mask, so the shoreline is not the 2 m grid. */
function buildWater(hole: Hole, pal: Palette): THREE.Mesh | null {
  const n = hole.nx * hole.nz;
  const waterId = SURFACES.indexOf('water');
  const mask = new Float32Array(n);
  let count = 0, sumH = 0;
  for (let k = 0; k < n; k++) if (hole.surfaces[k] === waterId) { mask[k] = 1; count++; sumH += hole.heights[k]; }
  if (count < 6) return null;

  const blur = new Float32Array(n);
  for (let j = 0; j < hole.nz; j++) for (let i = 0; i < hole.nx; i++) {
    let s = 0, w = 0;
    for (let dj = -1; dj <= 1; dj++) for (let di = -1; di <= 1; di++) {
      const jj = j + dj, ii = i + di;
      if (jj < 0 || ii < 0 || jj >= hole.nz || ii >= hole.nx) continue;
      const wt = di === 0 && dj === 0 ? 2 : 1;
      s += mask[jj * hole.nx + ii] * wt; w += wt;
    }
    blur[j * hole.nx + i] = s / w;
  }
  const data = new Uint8Array(n);
  for (let k = 0; k < n; k++) data[k] = Math.round(blur[k] * 255);
  const tex = new THREE.DataTexture(data, hole.nx, hole.nz, THREE.RedFormat);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;

  const level = sumH / count + 0.62;
  const geo = new THREE.PlaneGeometry(hole.widthM, hole.lengthM, Math.min(160, hole.nx * 2), Math.min(300, hole.nz * 2));
  geo.rotateX(-Math.PI / 2);
  const mat = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, fog: true,
    uniforms: {
      uTime: { value: 0 }, uMask: { value: tex },
      uShallow: { value: col(pal.water.shallow) }, uDeep: { value: col(pal.water.deep) },
      uSky: { value: col(pal.sky[1]) }, uSun: { value: col(pal.sun) },
      uSunDir: { value: new THREE.Vector3(0, 0.4, -1) },
      fogColor: { value: col(pal.fog) }, fogNear: { value: pal.fogNear }, fogFar: { value: pal.fogFar },
    },
    vertexShader: /* glsl */ `
      uniform float uTime; varying vec2 vUv; varying vec3 vW; varying float vFogDepth;
      void main(){
        vUv = uv; vec3 p = position;
        p.y += sin(p.x * 0.55 + uTime * 1.3) * 0.035 + sin(p.z * 0.42 - uTime * 0.9) * 0.045;
        vec4 wp = modelMatrix * vec4(p, 1.0); vW = wp.xyz;
        vec4 mv = viewMatrix * wp; vFogDepth = -mv.z;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform sampler2D uMask; uniform vec3 uShallow, uDeep, uSky, uSun, uSunDir, fogColor;
      uniform float uTime, fogNear, fogFar;
      varying vec2 vUv; varying vec3 vW; varying float vFogDepth;
      ${NOISE}
      void main(){
        float m = texture2D(uMask, vUv).r;
        float a = smoothstep(0.18, 0.58, m);
        if (a < 0.02) discard;
        vec2 q = vW.xz;
        float w1 = sin(q.x * 0.9 + uTime * 1.6) * 0.5 + 0.5;
        float w2 = sin((q.x * 0.5 + q.y * 0.85) * 1.4 - uTime * 1.1) * 0.5 + 0.5;
        float ripple = w1 * 0.5 + w2 * 0.5 + fbm(q * 1.3 + uTime * 0.12) * 0.5;
        vec3 nrm = normalize(vec3((w1 - 0.5) * 0.30, 1.0, (w2 - 0.5) * 0.30));
        vec3 base = mix(uShallow, uDeep, smoothstep(0.35, 0.95, m));
        vec3 view = normalize(cameraPosition - vW);
        float fres = pow(1.0 - max(dot(view, nrm), 0.0), 3.0);
        vec3 c = mix(base, uSky, clamp(fres * 1.15, 0.0, 0.85));
        vec3 h = normalize(normalize(uSunDir) + view);
        c += uSun * pow(max(dot(nrm, h), 0.0), 220.0) * 1.6;
        c += uSun * smoothstep(0.86, 1.0, ripple) * 0.10;
        c = mix(c, fogColor, smoothstep(fogNear, fogFar, vFogDepth));
        gl_FragColor = vec4(c, a * (0.88 + fres * 0.12));
      }`,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'water';
  mesh.position.set(0, level, hole.lengthM / 2);
  mesh.renderOrder = 1;
  mesh.userData.tick = (t: number, world?: World) => {
    mat.uniforms.uTime.value = t;
    if (world) mat.uniforms.uSunDir.value.copy(world.sunDir);
  };
  return mesh;
}

/* ------------------------------------------------------------ cup and flag */

function buildCup(hole: Hole): THREE.Group {
  const g = new THREE.Group();
  g.name = 'cup';
  const hollow = new THREE.Mesh(
    new THREE.CylinderGeometry(0.108, 0.108, 0.34, 20, 1, true),
    new THREE.MeshStandardMaterial({ color: 0x14180f, roughness: 1, side: THREE.BackSide }),
  );
  hollow.position.y = -0.17;
  const floor = new THREE.Mesh(new THREE.CircleGeometry(0.108, 20), new THREE.MeshBasicMaterial({ color: 0x090b07 }));
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -0.33;
  const rim = new THREE.Mesh(
    new THREE.RingGeometry(0.108, 0.135, 24),
    new THREE.MeshBasicMaterial({ color: 0xf3f4ee, transparent: true, opacity: 0.85 }),
  );
  rim.rotation.x = -Math.PI / 2;
  rim.position.y = 0.006;
  g.add(hollow, floor, rim);
  g.position.set(hole.cup.x, sampleHeight(hole, hole.cup.x, hole.cup.z), hole.cup.z);
  return g;
}

function buildFlag(hole: Hole, pal: Palette): THREE.Group {
  const flag = new THREE.Group();
  flag.name = 'flag';
  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.016, 0.02, 2.4, 10),
    new THREE.MeshStandardMaterial({ color: 0xf2f2ec, roughness: 0.5, metalness: 0.1 }),
  );
  pole.position.y = 1.2;
  pole.castShadow = true;
  flag.add(pole);
  for (const y of [0.62, 0.9]) {
    const band = new THREE.Mesh(new THREE.CylinderGeometry(0.021, 0.021, 0.07, 10), new THREE.MeshStandardMaterial({ color: 0x1c1f16, roughness: 0.8 }));
    band.position.y = y;
    flag.add(band);
  }
  const finial = new THREE.Mesh(new THREE.SphereGeometry(0.035, 12, 8), new THREE.MeshStandardMaterial({ color: 0xf2f2ec, roughness: 0.4 }));
  finial.position.y = 2.41;
  flag.add(finial);

  // The cloth is the wind instrument: `state.wind` sets how hard it snaps and which way it points.
  const clothGeo = new THREE.PlaneGeometry(0.62, 0.4, 16, 8);
  clothGeo.translate(0.31, 0, 0);
  const clothMat = new THREE.MeshStandardMaterial({ color: col(pal.accent), roughness: 0.9, side: THREE.DoubleSide });
  clothMat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = { value: 0 };
    shader.uniforms.uWind = { value: 0.6 };
    clothMat.userData.shader = shader;
    shader.vertexShader = `uniform float uTime; uniform float uWind;\n${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
       float k = transformed.x / 0.62;
       float amp = uWind * k * k;
       transformed.z += sin(transformed.x * 9.0 - uTime * 7.0) * 0.11 * amp;
       transformed.y += sin(transformed.x * 6.0 - uTime * 5.5 + transformed.y * 3.0) * 0.05 * amp;
       transformed.x -= 0.06 * amp * k;`,
    );
  };
  const cloth = new THREE.Mesh(clothGeo, clothMat);
  cloth.position.set(0.02, 2.16, 0);
  cloth.castShadow = true;
  cloth.name = 'flag-cloth';
  flag.add(cloth);

  flag.position.set(hole.cup.x, sampleHeight(hole, hole.cup.x, hole.cup.z), hole.cup.z);
  flag.userData.tick = (t: number, _world?: World, wind?: { x: number; z: number }) => {
    const shader = clothMat.userData.shader as { uniforms: Record<string, THREE.IUniform> } | undefined;
    if (!shader) return;
    shader.uniforms.uTime.value = t;
    const speed = wind ? Math.hypot(wind.x, wind.z) : 1.5;
    shader.uniforms.uWind.value = 0.25 + Math.min(1.3, speed / 5);
    if (wind && (wind.x || wind.z)) cloth.rotation.y = Math.atan2(wind.x, wind.z) - Math.PI / 2;
  };
  return flag;
}

/* ------------------------------------------------------------- tee and posts */

function buildTee(hole: Hole, pal: Palette): THREE.Group {
  const g = new THREE.Group();
  g.name = 'tee-markers';
  const y = sampleHeight(hole, hole.tee.x, hole.tee.z);
  for (const dx of [-1.4, 1.4]) {
    const m = new THREE.Mesh(
      new THREE.CylinderGeometry(0.055, 0.11, 0.3, 8),
      new THREE.MeshStandardMaterial({ color: col(pal.accent), roughness: 0.7 }),
    );
    m.position.set(hole.tee.x + dx, sampleHeight(hole, hole.tee.x + dx, hole.tee.z) + 0.15, hole.tee.z);
    m.castShadow = true;
    g.add(m);
  }
  const pad = new THREE.Mesh(
    new THREE.PlaneGeometry(4.6, 3.0),
    new THREE.MeshStandardMaterial({ color: col(pal.turf.tee).multiplyScalar(1.06), roughness: 1, transparent: true, opacity: 0.55 }),
  );
  pad.rotation.x = -Math.PI / 2;
  pad.position.set(hole.tee.x, y + 0.015, hole.tee.z);
  g.add(pad);
  return g;
}

function numberPlate(text: string, w: number, h: number, bg: string, fg: string, size: number): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = 256;
  cv.height = Math.round((256 * h) / w);
  const ctx = cv.getContext('2d')!;
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, cv.width, cv.height);
  ctx.fillStyle = fg;
  ctx.font = `700 ${size}px ui-sans-serif, system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cv.width / 2, cv.height / 2 + 2);
  const tex = new THREE.CanvasTexture(cv);
  tex.anisotropy = 4;
  return tex;
}

/** 200 / 150 / 100 m posts either side of the fairway spine — the eye's yardage book. */
function buildDistancePosts(hole: Hole, pal: Palette): THREE.Group | null {
  const g = new THREE.Group();
  g.name = 'distance-posts';
  let any = false;
  for (const d of [200, 150, 100]) {
    const z = hole.cup.z - d;
    if (z < hole.tee.z + 25) continue;
    const cx = hole.centerline.reduce((a, p) => (Math.abs(p.z - z) < Math.abs(a.z - z) ? p : a), hole.centerline[0]).x;
    for (const side of [-1, 1]) {
      const x = cx + side * (hole.brief.par === 3 ? 11 : 14);
      if (Math.abs(x) > hole.widthM / 2 - 3) continue;
      const y = sampleHeight(hole, x, z);
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.035, 0.045, 1.0, 6),
        new THREE.MeshStandardMaterial({ color: 0xf0efe6, roughness: 0.8 }),
      );
      post.position.set(x, y + 0.5, z);
      post.castShadow = true;
      const plate = new THREE.Mesh(
        new THREE.PlaneGeometry(0.42, 0.26),
        new THREE.MeshBasicMaterial({ map: numberPlate(String(d), 0.42, 0.26, pal.accent, '#fdfaf2', 110), side: THREE.DoubleSide, transparent: true }),
      );
      plate.position.set(x, y + 1.08, z);
      g.add(post, plate);
      any = true;
    }
  }
  return any ? g : null;
}

/* -------------------------------------------------------------------- trees */

function treeGeometry(kind: Palette['tree']): THREE.BufferGeometry {
  if (kind === 'pine') {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 3; i++) {
      const c = new THREE.ConeGeometry(1.15 - i * 0.22, 0.75, 7);
      c.translate(i * 0.14, 1.35 + i * 0.5, 0);
      c.scale(1, 0.8, 1);
      parts.push(c);
    }
    return mergeGeometries(parts);
  }
  if (kind === 'conifer') {
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 3; i++) {
      const c = new THREE.ConeGeometry(1.1 - i * 0.3, 1.5 - i * 0.2, 7);
      c.translate(0, 0.9 + i * 0.72, 0);
      parts.push(c);
    }
    return mergeGeometries(parts);
  }
  if (kind === 'mound') {
    const g = new THREE.IcosahedronGeometry(1, 1);
    g.scale(1.25, 0.55, 1.1);
    g.translate(0, 0.4, 0);
    return g;
  }
  if (kind === 'scrub') {
    const parts: THREE.BufferGeometry[] = [];
    for (const [dx, dz, s] of [[0, 0, 1], [0.7, 0.3, 0.66], [-0.5, 0.6, 0.55]]) {
      const g = new THREE.IcosahedronGeometry(s, 0);
      g.scale(1, 0.7, 1);
      g.translate(dx, 0.45 * s, dz);
      parts.push(g);
    }
    return mergeGeometries(parts);
  }
  const g = new THREE.IcosahedronGeometry(1, 1);
  g.scale(1.05, 1.15, 1.05);
  g.translate(0, 1.75, 0);
  return g;
}

/** Two instanced meshes for the whole tree line, planted only on rough and out-of-bounds cells. */
function buildTrees(hole: Hole, pal: Palette, rnd: () => number): THREE.Group | null {
  const spots: { x: number; z: number; s: number; r: number; c: number }[] = [];
  const wants = pal.treeCount;
  for (let tries = 0; tries < wants * 6 && spots.length < wants; tries++) {
    const z = 12 + rnd() * (hole.lengthM - 20);
    const cx = hole.centerline.reduce((a, p) => (Math.abs(p.z - z) < Math.abs(a.z - z) ? p : a), hole.centerline[0]).x;
    const side = rnd() < 0.5 ? -1 : 1;
    const x = cx + side * (13 + rnd() * 18);
    if (Math.abs(x) > hole.widthM / 2 - 1) continue;
    const s = sampleSurface(hole, x, z);
    if (s !== 'rough' && s !== 'oob') continue;
    if (Math.hypot(x - hole.cup.x, z - hole.cup.z) < hole.greenRadius + 6) continue;
    if (Math.hypot(x - hole.tee.x, z - hole.tee.z) < 10) continue;
    spots.push({
      x, z,
      s: pal.treeScale[0] + rnd() * (pal.treeScale[1] - pal.treeScale[0]),
      r: rnd() * Math.PI * 2,
      c: Math.floor(rnd() * pal.treeColors.length),
    });
  }
  if (!spots.length) return null;

  // A vertex-shader sway, so a hundred and fifty trees cost nothing to animate.
  const sway = (mat: THREE.MeshStandardMaterial, amount: number) => {
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = { value: 0 };
      mat.userData.shader = shader;
      shader.vertexShader = `uniform float uTime;\n${shader.vertexShader}`.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
         #ifdef USE_INSTANCING
           vec3 iw = vec3(instanceMatrix[3][0], instanceMatrix[3][1], instanceMatrix[3][2]);
         #else
           vec3 iw = vec3(0.0);
         #endif
         float bend = pow(max(transformed.y, 0.0), 1.35) * ${amount.toFixed(3)};
         transformed.x += sin(uTime * 0.8 + iw.z * 0.22 + iw.x * 0.17) * bend;
         transformed.z += cos(uTime * 0.65 + iw.x * 0.19) * bend * 0.6;`,
      );
    };
  };

  const canopyMat = new THREE.MeshStandardMaterial({ roughness: 1, flatShading: true });
  sway(canopyMat, 0.02);
  const trunkMat = new THREE.MeshStandardMaterial({ color: col(pal.trunk), roughness: 1, flatShading: true });
  sway(trunkMat, 0.006);

  const trunkGeo = new THREE.CylinderGeometry(0.1, 0.16, 1.9, 6);
  trunkGeo.translate(0, 0.95, 0);
  const canopy = new THREE.InstancedMesh(treeGeometry(pal.tree), canopyMat, spots.length);
  const trunk = new THREE.InstancedMesh(trunkGeo, trunkMat, spots.length);
  canopy.castShadow = true;
  canopy.receiveShadow = true;
  trunk.castShadow = true;

  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), v = new THREE.Vector3(), sc = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const tint = new THREE.Color();
  spots.forEach((p, i) => {
    const y = sampleHeight(hole, p.x, p.z);
    q.setFromAxisAngle(up, p.r);
    v.set(p.x, y, p.z);
    sc.set(p.s * (0.85 + (0.3 * (i % 3)) / 2), p.s, p.s * (0.85 + (0.3 * ((i + 1) % 3)) / 2));
    m.compose(v, q, sc);
    canopy.setMatrixAt(i, m);
    sc.set(p.s * 0.5, p.s * 0.55, p.s * 0.5);
    m.compose(v, q, sc);
    trunk.setMatrixAt(i, m);
    tint.set(pal.treeColors[p.c]).offsetHSL(0, 0, (i % 5) * 0.012 - 0.024);
    canopy.setColorAt(i, tint);
  });
  canopy.instanceMatrix.needsUpdate = true;
  if (canopy.instanceColor) canopy.instanceColor.needsUpdate = true;
  trunk.instanceMatrix.needsUpdate = true;

  const g = new THREE.Group();
  g.name = 'trees';
  g.add(canopy);
  if (pal.tree !== 'mound' && pal.tree !== 'scrub') g.add(trunk);
  g.userData.tick = (t: number) => {
    for (const mat of [canopyMat, trunkMat]) {
      const shader = mat.userData.shader as { uniforms: Record<string, THREE.IUniform> } | undefined;
      if (shader) shader.uniforms.uTime.value = t;
    }
  };
  return g;
}

/* ------------------------------------------------------------- aim and power */

/** The aim line follows the ground, so a blind shot over a ridge reads as one. */
export function makeAimLine(): THREE.Line {
  const N = 48;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(N * 3), 3));
  geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(N * 3), 3));
  const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.9, depthWrite: false }));
  line.name = 'aim';
  line.renderOrder = 2;
  line.frustumCulled = false;
  return line;
}

export function updateAimLine(
  line: THREE.Line,
  hole: Hole,
  from: { x: number; z: number },
  aimDeg: number,
  lengthM: number,
  accent = '#ffffff',
) {
  const a = (aimDeg * Math.PI) / 180;
  const pos = line.geometry.attributes.position as THREE.BufferAttribute;
  const cols = line.geometry.attributes.color as THREE.BufferAttribute;
  const N = pos.count;
  const c = new THREE.Color(accent);
  for (let i = 0; i < N; i++) {
    const f = i / (N - 1);
    const d = f * lengthM;
    const x = from.x + Math.sin(a) * d, z = from.z + Math.cos(a) * d;
    pos.setXYZ(i, x, sampleHeight(hole, x, z) + 0.06, z);
    // a dashed feel without LineDashedMaterial: alternate segments toward transparent-ish
    const dash = i % 4 < 2 ? 1 : 0.15;
    const fade = (1 - f * 0.85) * dash;
    cols.setXYZ(i, c.r * fade + 0.27 * fade, c.g * fade + 0.27 * fade, c.b * fade + 0.27 * fade);
  }
  pos.needsUpdate = true;
  cols.needsUpdate = true;
}

/** The same line, laid along a simulated path — the putt preview (2026-09-13). The player sees the
 *  break and where the ball stops, which is the whole of reading a green. The line's point budget
 *  is fixed, so the samples are resampled to it. */
export function updatePathLine(line: THREE.Line, hole: Hole, samples: { x: number; z: number }[], accent = '#ffffff') {
  const pos = line.geometry.attributes.position as THREE.BufferAttribute;
  const cols = line.geometry.attributes.color as THREE.BufferAttribute;
  const N = pos.count;
  const c = new THREE.Color(accent);
  const n = samples.length;
  for (let i = 0; i < N; i++) {
    const f = i / (N - 1);
    const s = samples[Math.min(n - 1, Math.floor(f * (n - 1)))];
    pos.setXYZ(i, s.x, sampleHeight(hole, s.x, s.z) + 0.06, s.z);
    const dash = i % 4 < 2 ? 1 : 0.15;
    const fade = (1 - f * 0.5) * dash;
    cols.setXYZ(i, c.r * fade + 0.27 * fade, c.g * fade + 0.27 * fade, c.b * fade + 0.27 * fade);
  }
  pos.needsUpdate = true;
  cols.needsUpdate = true;
}

export interface PowerArc extends THREE.Group {
  userData: { set: (power: number, aimDeg: number) => void };
}

/** The swing meter on the ground at the ball: the VR player's power bar, and a nice second read on a screen. */
export function makePowerArc(accent = '#e0b13c'): PowerArc {
  const g = new THREE.Group() as PowerArc;
  g.name = 'power-arc';
  const span = Math.PI * 0.62;
  const track = new THREE.Mesh(
    new THREE.RingGeometry(1.25, 1.42, 48, 1, -span / 2 + Math.PI / 2, span),
    new THREE.MeshBasicMaterial({ color: 0x0e120d, transparent: true, opacity: 0.3, side: THREE.DoubleSide, depthWrite: false }),
  );
  track.rotation.x = -Math.PI / 2;
  g.add(track);
  const fillMat = new THREE.MeshBasicMaterial({ color: col(accent), transparent: true, opacity: 0.95, side: THREE.DoubleSide, depthWrite: false });
  let fill: THREE.Mesh | null = null;
  g.userData = {
    set(power: number, aimDeg: number) {
      if (fill) { g.remove(fill); fill.geometry.dispose(); }
      const amt = Math.max(0.001, Math.min(1, power)) * span;
      fill = new THREE.Mesh(new THREE.RingGeometry(1.25, 1.42, 48, 1, -span / 2 + Math.PI / 2, amt), fillMat);
      fill.rotation.x = -Math.PI / 2;
      g.add(fill);
      g.rotation.y = -((aimDeg * Math.PI) / 180) + span / 2;
    },
  };
  g.renderOrder = 3;
  g.visible = false;
  return g;
}
